import React from 'react';
import { Modal } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons'; // Import the Ant Design icon
import PrimaryButton from './primaryButton';
import { useTranslation } from 'react-i18next';

interface CustomModalProps {
  isVisible: boolean;
  title: string;
  description: string;
  onOk?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onCancel?: () => void;
  iconType: 'Warning' | 'Error' | 'Success' | 'Info' | string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  closable?: boolean;
  zindex?: number;
}

const iconUrls = {
  Warning: '/warning.svg',
  Error: '/error.svg',
  Success: '/success.svg',
};

const CustomModal: React.FC<CustomModalProps> = ({
  isVisible,
  title,
  description,
  onOk,
  onCancel,
  iconType,
  primaryButtonText,
  secondaryButtonText,
  closable = true,
  zindex
}) => {
  const iconUrl = iconUrls[iconType];

  // Determine the width based on content length
  const determineModalWidth = () => {
    if (description?.length < 150) {
      return '520px';
    } else {
      return '100%';
    }
  };
  const { t } = useTranslation();
  return (
    <Modal
      data-testid="custom-modal"
      open={isVisible}
      onCancel={onCancel}
      footer={null}
      centered
      className="w-full"
      style={{
        maxWidth: '800px',
        width: determineModalWidth(),
        margin: '0 auto',
        minWidth: '285px',
      }}
      width={determineModalWidth()}
      closable={closable}
      zIndex={zindex}
    >
      <div className="flex flex-col justify-center items-center py-4 px-6 text-center">
        {iconType === 'Info' ? (
          <InfoCircleOutlined
            style={{ fontSize: '78px', color: '#FDB022' }}
            className="mb-4"
            data-testid="modal-icon"
          />
        ) : (
          <img
            src={iconUrl}
            alt="ModalIcon"
            className="mb-4"
            data-testid="modal-icon"
            width={66}
            height={66}
          />
        )}
        <div
          className="text-[22px] text-black font-medium mb-4"
          data-testid="modal-title"
        >
          {t(title)}
        </div>
        <p
          className="text-[#000000] text-base mb-4"
          style={{ whiteSpace: 'pre-line' }}
          data-testid={`modal-${description}`}
        >
          {t(description)}
        </p>
        <div
          className={`${secondaryButtonText ? 'justify-between' : 'justify-center'} flex items-center gap-4`}
          data-testid="modal-buttons"
        >
          {primaryButtonText && (
            <PrimaryButton
              data-testid={`${primaryButtonText}`}
              theme="secondary"
              secondaryColor={{
                backgroundColor: 'transparent',
                color: '#1D4E89',
                borderColor: '#1D4E89',
              }}
              disabled={false}
              height={'4vmin'}
              onClick={onOk}
              sizeType="small"
              className="w-full font-medium mt-3"
              //data-testid="modal_yes-button"
            >
              {t(primaryButtonText)}
            </PrimaryButton>
          )}
          {secondaryButtonText && (
            <PrimaryButton
              theme="secondary"
              secondaryColor={{
                backgroundColor: 'transparent',
                color: '#0298A0',
                borderColor: '#0298A0',
              }}
              disabled={false}
              height={'4vmin'}
              onClick={onCancel}
              sizeType="small"
              className="w-full font-medium mt-3"
              data-testid={`${secondaryButtonText}`}
            >
              {t(secondaryButtonText)}
            </PrimaryButton>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CustomModal;
